import React, { useEffect, useState } from "react";
import styles from "./css/CreateProjectPage.module.css";
import { useTranslation } from "react-i18next";
import { CreateProjectData, Project } from "@/types/project";
import { Button } from "@/components/Buttons";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/store/store";
import { BackButton } from "@/components/Buttons/BackButton";
import { FilesTypes, UploadFilesTableData } from "@/types/file";
import { currentDate } from "@/utils/date";
import { CreateAlternativeRequest } from "@/types/alternative";
import { CreateProject } from "@/components/pageParts/project/CreateProject";
import { CreateAlternative } from "@/components/pageParts/project/CreateAlternative";
import { useCreateProject } from "@/hooks/project/useCreateProject";
import { useCreateAlternative } from "@/hooks/alternative/useCreateAlternative";
import { useFetchAllEntities, useFetchAllProjects } from "@/utils/fetchData";
import { getEntityIdPair } from "@/utils/getEntityPairId";
import { useDeleteProject } from "@/hooks/project/useDeleteProject";
import { setToast } from "@/store/systemSlice";
import { useExtractModelData } from "@/hooks/alternative/useExtractModelData";
import { useUpdateProject } from "@/hooks/project/useUpdateProject";
import { useGetCityAttributes } from "@/hooks/cityAttribtes/useGetCityAttribute";
import { CityAttributes } from "@/types/cityAttributes";
import GenericConfirmModal from "@/components/modals/GenericModal";


export const CreateProjectPage = () => {
    const navigate = useNavigate();
    const {t} = useTranslation("CreateProjectPage");
    const {entityName, cityId} = useParams<{entityName: string, cityId: string}>();
    const city = useSelector((state: RootState) => state.entity.cities.find((city) => city.id === cityId));
    const {userAo, userCity, entityType} = useSelector((state: RootState) => state.entity);
    const [step, setStep] = useState<number>(1);
    const [stepsCompleted, setStepsCompleted] = useState<boolean[]>([false, true, true, true, true]);
    const [projectFieldsCompleted, setProjectFieldsCompleted] = useState<boolean>(false);
    const [alternativeFieldsCompleted, setAlternativeFieldsCompleted] = useState<boolean>(false);
    const [createLoading, setCreateLoading] = useState<boolean>(false);
    const dispatch = useDispatch();
    const fetchAllProjects = useFetchAllProjects();
    const {getAllCities} = useFetchAllEntities();
    const deleteProject = useDeleteProject();
    const [modelDataLoading, setModelDataLoading] = useState<boolean>(true);
    const [cityAttributes, setCityAttributes] = useState<CityAttributes | null>(null);
    const projects = useSelector((state: RootState) => state.projects.projects);
    const [isProjectNameExists, setIsProjectNameExists] = useState<boolean>(false);

    const [isOpen, setIsOpen] = useState<boolean>(false);

    const getCityAttributes = useGetCityAttributes({
    onSuccess: (data) => {
        setCityAttributes(data);
    },
    onError: (error) => {
      console.error("Error in get City Attributes");
    }
  });

    useEffect(() => {
        if (cityId) {
            getCityAttributes.mutate({city_id: cityId, ao_id: userAo?.id as string});
        }
    }, [cityId]);

    useEffect(() => {
        if (cityAttributes?.charging_standard) {
            setCreateProjectData(prev => ({
                ...prev,
                residential_charging_standard: prev.residential_charging_standard === undefined ? 
                    cityAttributes?.charging_standard?.residential_charging_standard : 
                    prev.residential_charging_standard,
                other_uses_charging_standard: prev.other_uses_charging_standard === undefined ? 
                    cityAttributes?.charging_standard?.commercial_charging_standard : 
                    prev.other_uses_charging_standard
            }));
        }
    }, [cityAttributes]);

    const projectSuccess = () => {
        const entityIdPair = getEntityIdPair(entityType, userCity, userAo);
        fetchAllProjects.mutate(entityIdPair);
        getAllCities.mutate(entityIdPair);
        dispatch(
            setToast({
              toastTitle: t('projectCreatedSuccess'),
              toastMessage: t('dataProcessingMessage'),
              toastType: "success",
            })
          );
        navigate(`/${entityName}/projects/${cityId}`);
    }

    const updateProject = useUpdateProject({
        onSuccess(data) {
            projectSuccess();
        },
        onError(error) {
            projectSuccess();
        }
    })

    const createAlternative = useCreateAlternative({
        onSuccess(data) {
            setCreateLoading(false);
            projectSuccess();
            // updateProject.mutate({project_id: createAlternativeData.project_id, favorite_alternative: data.alternative_id, ...getEntityIdPair(entityType, userCity, userAo)});
        },
        onError(error) {
            setCreateLoading(false);
            // deleteProject.mutate({city_id: cityId, project_id: createAlternativeData.project_id, ...getEntityIdPair(entityType, userCity, userAo)});
            dispatch(
                setToast({
                  toastTitle: error.response.data.message,
                  toastMessage: "",
                  toastType: "error",
                })
              );
        }
    })

    const createProject = useCreateProject({
        onSuccess(data) {

            setCreateProjectData({...createProjectData, project_id: data.project_id});
            setCreateLoading(false);
        },
        onError(error) {
            dispatch(
                setToast({
                  toastTitle: error.response.data.message,
                  toastMessage: "",
                  toastType: "error",
                })
              );
            setCreateLoading(false);
        }
    })

    const [projectFiles, setProjectFiles] = useState<UploadFilesTableData[]>([
        {
            type: "blue line",
            file: null,
            file_name: "",
            created_at: "",
            accept_types: [".geojson", ".dxf", ".shapefile"]
        }
    ]);
    const [alternativeFiles, setAlternativeFiles] = useState<UploadFilesTableData[]>([
        {
            type: "3dmodel",
            file: null,
            file_name: "",
            created_at: "",
            accept_types: [".3dm", ".glb", ".gltf", ".obj"]
        },        
    ]);

    const [createProjectData, setCreateProjectData] = useState<CreateProjectData>({
        name: "",
        description: "",
        ao_id: userAo?.id as string,
        city_id: cityId as string,
        neighborhood_id: "",
        logo: null,
        files: {},
        residents_initial: undefined,
        block: undefined,
        plot: undefined,

        vehicles_initial: undefined,

     
        current_energy_production: undefined,
        residential_area_existing: undefined,
        residental_unit_area_existing: undefined,
        commercial_area_existing: undefined,
        public_building_area_existing: undefined,
        industrial_area_existing: undefined,

        residential_charging_standard: undefined,
        other_uses_charging_standard: undefined,
    });

    const [createAlternativeData, setCreateAlternativeData] = useState<CreateAlternativeRequest>({
        name: "",
        description: "",
        ao_id: userAo?.id as string,
        city_id: cityId as string,
        neighborhood_id: "",
        project_id: "",
        residential_area_planned: undefined,
        residental_unit_area_planned: undefined,
        commercial_area_planned: undefined,
        public_building_area_planned: undefined,
        industrial_area_planned: undefined,

        planned_additional_production: undefined,
        building_energy_rating: "B",

        roofs_area: undefined,
        facades_area_north: undefined,
        facades_area_south: undefined,
        facades_area_east: undefined,
        facades_area_west: undefined,
        facades_area_total: undefined,
        future_residential_parking: undefined,
        future_commercial_parking: undefined,
        future_industrial_parking: undefined,
        future_public_parking: undefined,
        residents_final_target_year: undefined
    });


    const extractModelData = useExtractModelData({
        onSuccess(data) {
            setCreateAlternativeData({
                ...createAlternativeData,
                roofs_area: data.total_roof_area,
                facades_area_north: data.total_facades.north,
                facades_area_south: data.total_facades.south,
                facades_area_east: data.total_facades.east,
                facades_area_west: data.total_facades.west,
                facades_area_total: data.total_facades.total
            });
            setModelDataLoading(false);
        },
        onError(error) {
            console.error("Error in extract Model Data");
            setModelDataLoading(false);
        }
    });

    useEffect(() => {
        if (!city && !userCity) {
            navigate("/404");
        }
    }, [city]);

    const handleCreateProjectData = (data: CreateProjectData) => {
        // Check if the name exists in other projects
        const nameExists = projects.some(project => project.name === data.name);
        setIsProjectNameExists(nameExists);
        setCreateProjectData(data);
        
        // Update steps completion based on name existence
        setStepsCompleted(prev => {
            const newSteps = [...prev];
            newSteps[0] = Boolean(data.name) && !nameExists && projectFieldsCompleted;
            return newSteps;
        });
    }

    const handleCreateAlternativeData = (data: CreateAlternativeRequest) => {
        const newData = {
            ...data, 
            facades_area_total: (data.facades_area_north || 0) + 
                              (data.facades_area_south || 0) + 
                              (data.facades_area_east || 0) + 
                              (data.facades_area_west || 0)
        };
        setCreateAlternativeData(newData);
    }
    const handleGoBack = () => {
        if (step === 1) {
            return;
        }
        if (step === 2) {
            setIsOpen(true);
            return;
        }
        setStep(step - 1);
    }

    const handleModalConfirm = () => {
        setIsOpen(false);
        fetchAllProjects.mutate(getEntityIdPair(entityType, userCity, userAo));
        navigate(`/${entityName}/project/${cityId}/${createProjectData.project_id}`);
    }

    const handleModalCancel = () => {
        setIsOpen(false);
    }

    const handleContinue = () => {
        if (step === 1) {
            setCreateLoading(true);
            createProject.mutate({
                ...createProjectData,
                files: projectFiles
                .filter(file => file.file)
                .reduce((acc, file) => ({
                    ...acc,
                    [file.type]: file.file
                }), {} as Record<FilesTypes, File>)
            });
            setStep(step + 1);
        } else {
            setStep(step);
            setCreateLoading(true)
            const alternativeData = {
                ...createAlternativeData,
                files: alternativeFiles
                .filter(file => file.file)
                .reduce((acc, file) => ({
                    ...acc,
                    [file.type]: file.file
                }), {} as Record<FilesTypes, File>),
                project_id: createProjectData.project_id as string
            }
            setCreateAlternativeData({...alternativeData, project_id: createProjectData.project_id as string});
            createAlternative.mutate(alternativeData);
        }
    }

    const handleUploadFile = (type: FilesTypes, file: File, project: boolean) => {
        if (project) {
            const newProjectFiles = [...projectFiles];
            const index = newProjectFiles.findIndex(file => file.type === type);
            newProjectFiles[index] = {type, file, file_name: file.name, created_at: currentDate(), accept_types: projectFiles[index].accept_types};
            setProjectFiles(newProjectFiles);
        } else {
            const newAlternativeFiles = [...alternativeFiles];
            const index = newAlternativeFiles.findIndex(file => file.type === type);
            newAlternativeFiles[index] = {type, file, file_name: file.name, created_at: currentDate(), accept_types: alternativeFiles[index].accept_types};
            setAlternativeFiles(newAlternativeFiles);
            if (type === "3dmodel") {
                extractModelData.mutate({files: {"3dmodel": file}, ...getEntityIdPair(entityType, userCity, userAo)});
            }
        }
    }

    return (
        <div className={styles.container}>
            <div className={styles.headerContainer}>
                <h1 className={styles.title}>{step == 1 ?t("createProject"): t("createAlternativeTitle")}</h1>
                <div className={styles.backButtonContainer}>
                    <BackButton/>
                </div>
            </div>
            <div className={styles.formContainer}>
                {step === 1 && <CreateProject onChange={handleCreateProjectData} data={createProjectData} setStepsCompleted={setStepsCompleted} projectFiles={projectFiles} onUploadFile={(type: FilesTypes, file: File) => handleUploadFile(type, file, true)} fieldsCompleted={projectFieldsCompleted} setFieldsCompleted={setProjectFieldsCompleted} isProjectNameExists={isProjectNameExists}/>}
                {step === 2 && <CreateAlternative disabled={modelDataLoading} onChange={handleCreateAlternativeData} data={createAlternativeData} setStepsCompleted={setStepsCompleted} alternativeFiles={alternativeFiles} onUploadFile={(type: FilesTypes, file: File) => handleUploadFile(type, file, false)} fieldsCompleted={alternativeFieldsCompleted} setFieldsCompleted={setAlternativeFieldsCompleted}/>}
                <div className={styles.stepsContainerOnePage}>
                    {isOpen && 
                        <GenericConfirmModal 
                            isOpen={isOpen} 
                            title={t('returnToProjectTitle')} 
                            description={t('returnToProjectDesc')} 
                            confirmText={t('returnToProjectConfirm')} 
                            cancelText={t('continueCreatingAlternative')} 
                            onConfirm={handleModalConfirm} 
                            onCancel={handleModalCancel} 
                            loading={false} 
                            type="escape"
                        />
                    }
                    <div className={styles.stepsSubContainer}>
                        <Button icon="no" text={t('goBack')} ready={true} color="gray" onClick={handleGoBack} id="goBack-btn" className={styles.goBackBtn} disabled={step === 1 || createLoading}/>
                        <Button icon="no" text={`${step === 2 ? t('finish') : t('continue')}`} onClick={handleContinue} id="continue-btn" className={styles.continueBtn} disabled={!stepsCompleted[step - 1] || createLoading} loading={createLoading}/>
                    </div>
                </div>
            </div>
        </div>
    );
}